<template>
  <div class="navbar">
    <!-- 菜单路由 -->
    <div class="navbar-tags-view">
      <TagsView />
    </div>

    <div class="right-menu flex-center">
      <!-- 代客模式下：允许不依赖客户权限返回管理员账号 -->
      <el-button v-if="isImpersonating" class="delegated-operation-btn" type="primary" plain size="mini" @click="cancelDelegatedOperation">
        {{ $t('cancelDelegatedOperationText') }}
      </el-button>
      <!-- 管理员模式下：有权限才允许进入代客操作 -->
      <el-button v-else v-permission:BusinessCustomerDelegatedOperation class="delegated-operation-btn" type="primary" plain size="mini" @click="showDelegatedOperationDialog">
        {{ $t('delegatedOperationText') }}
      </el-button>
      <!-- 用户手册 -->
      <home-help v-if="$store.getters.cusId" class="right-menu-item hover-effect header-icon" />
      <!-- 语言切换 -->
      <lang-select class="right-menu-item hover-effect header-icon" />
      <!-- 用户中心 -->
      <el-dropdown class="avatar-container right-menu-item hover-effect" trigger="click">
        <!-- 头像 -->
        <div class="avatar-wrapper">
          <BjKeyImage class="user-avatar" :url="avatar || $getPublicImage('logo-circle.png')" />
        </div>
        <el-dropdown-menu slot="dropdown">
          <!-- 角色 -->
          <el-dropdown-item>
            <el-form ref="form" label-width="45px" class="role-form" label-position="right">
              <el-form-item :label="$t('RoleText')+':'">
                <BjMyRoles @change="onChangeRole" />
              </el-form-item>
            </el-form>
          </el-dropdown-item>
          <!-- 账号 -->
          <el-dropdown-item>
            <el-form ref="form" class="role-form" label-position="right">
              <el-form-item :label="$t('AccountNumberText')+':'">
                <div class="user-group-form">
                  <span class="loginName">{{ userInfo.loginName }}</span>
                  <el-button v-if="showSwitch" plain @click="showUserDialog">{{ $t('changeText') }}</el-button>
                </div>
              </el-form-item>
            </el-form>
          </el-dropdown-item>
          <!-- 个人中心 -->
          <el-dropdown-item class="el-dropdown-menu__item--divided" @click.native="$toPage('CommonPersonal')">
            <svg-icon icon-class="user" />{{ $t('PersonalCenterText') }}
          </el-dropdown-item>
          <!-- 退出系统 -->
          <el-dropdown-item divided @click.native="logout">
            <span style="display: block"><svg-icon icon-class="logout" />{{ $t('ExitSystemText') }}</span>
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>
    <!-- 用户组列表弹窗 -->
    <UserDialog ref="UserDialog" @isShowSwitch="isShowSwitch"></UserDialog>
    <!-- 代客操作列表弹框 -->
    <DelegatedOperationDialog ref="DelegatedOperationDialog"></DelegatedOperationDialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import LangSelect from '@/components/LangSelect';
import HomeHelp from '@/components/HomeHelp';
import UserDialog from './UserDialog';
import DelegatedOperationDialog from './DelegatedOperationDialog';

import TagsView from './TagsView';
import { userApi } from '@/api';
import { getAdminToken, getImpersonationInfo, removeAdminToken, removeImpersonationInfo, setToken } from '@/utils/auth';

export default {
  components: {
    TagsView,
    LangSelect,
    HomeHelp,
    UserDialog,
    DelegatedOperationDialog,
  },
  data() {
    return {
      showSwitch: false,
      impersonationStatusVersion: 0,
    };
  },
  computed: {
    ...mapGetters(['avatar', 'userInfo']),
    isImpersonating() {
      this.impersonationStatusVersion;
      return !!(getAdminToken() || getImpersonationInfo());
    },
  },
  watch: {
    $route() {
      this.syncImpersonationStatus();
    },
  },
  mounted() {
    this.syncImpersonationStatus();
    window.addEventListener('focus', this.syncImpersonationStatus);
    window.addEventListener('storage', this.syncImpersonationStatus);
  },
  beforeDestroy() {
    window.removeEventListener('focus', this.syncImpersonationStatus);
    window.removeEventListener('storage', this.syncImpersonationStatus);
  },
  methods: {
    syncImpersonationStatus() {
      this.impersonationStatusVersion += 1;
    },
    onChangeRole(roleId) {
      userApi.changeRole({ roleId }).then(res => {
        this.$store.dispatch('tagsView/delAllViews').then(() => {
          location.href = location.href.replace(location.hash, '');
        });
      });
    },
    async logout() {
      await this.$store.dispatch('user/logout');
      sessionStorage.clear();
    },
    showUserDialog() {
      this.$refs.UserDialog.show();
    },
    showDelegatedOperationDialog() {
      this.$refs.DelegatedOperationDialog.show();
    },
    cancelDelegatedOperation() {
      this.$confirm(
        this.$t('confirmCancelDelegatedOperationText'),
        this.$t('toast.hintText'),
        {
          confirmButtonText: this.$t('toast.okText'),
          cancelButtonText: this.$t('toast.cancelText'),
          type: 'warning',
          customClass: 'delegated-operation-confirm',
        },
      ).then(() => {
        const adminToken = getAdminToken();
        if (!adminToken) {
          removeImpersonationInfo();
          this.syncImpersonationStatus();
          return;
        }
        setToken(adminToken);
        this.$store.dispatch('user/setToken', adminToken);
        removeAdminToken();
        removeImpersonationInfo();
        sessionStorage.clear();
        this.$store.dispatch('tagsView/delAllViews').finally(() => {
          location.href = location.href.replace(location.hash, '');
        });
      }).catch(() => {});
    },
    isShowSwitch(value) {
      this.showSwitch = value;
    },
  },
};
</script>

<style lang="scss" scoped>
.navbar {
  height: 40px;
  position: relative;
  display: flex;
  align-items: center;
  background: $background-dark;
  .navbar-tags-view {
    height: 40px;
    flex: 1;
    min-width: 0;
  }

  .right-menu {
    width: auto;
    min-width: 96px;
    flex-shrink: 0;
    height: 40px;
    padding-right: 20px;
    justify-content: flex-end;

    &:focus {
      outline: none;
    }

    .right-menu-item {
      display: inline-block;
      padding: 0 5px;
      font-size: 18px;
      color: #5a5e66;
      vertical-align: text-bottom;

      &.header-icon {
        display: inline-flex;
        align-items: center;
        height: 18px;
        line-height: 0;
      }

      &.hover-effect {
        cursor: pointer;
        transition: background 0.3s;

        &:hover {
          background: rgba(0, 0, 0, 0.025);
        }
      }
    }

    .delegated-operation-btn {
      margin-right: 8px;
      padding: 5px 10px;
    }

    .avatar-container {
      line-height: 40px;
      display: flex;
      justify-content: center;

      .avatar-wrapper {
        position: relative;
        display: flex;
        align-items: center;

        .user-avatar {
          cursor: pointer;
          width: 40px;
          height: 40px;
          border-radius: 50%;
        }

      }
    }
  }
}
.el-dropdown-menu svg {
  margin-right: 5px;
  font-size: 12px;
}
.el-dropdown-menu__item--divided {
  position: relative;
  margin-top: 6px;
  border-top: 1px solid #e6ebf5;
}
.role-form .el-form-item.el-form-item--mini {
  margin-bottom: 6px !important;
}
.user-group-form {
  display: flex;
  justify-content: space-between;
}
.loginName {
  padding-right: 10px;
}
</style>
